import React, { useEffect, useState } from 'react';
import ClariMeLogo from './ClariMeLogo';
import AppStoreBadge, { APP_STORE_URL } from './AppStoreBadge';

// iPadOS 13+ reports a Mac user agent; the touch-point check catches it.
function isAppleDevice() {
  const ua = navigator.userAgent || '';
  return /iPhone|iPad|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/**
 * #/get — the smart download link printed on clinic cards (QR target).
 *
 * iPhone/iPad visitors are forwarded straight to the App Store so scanning
 * the card is a one-step install. Everyone else (Android, desktop) lands on
 * a simple chooser: App Store badge for later + "use it in your browser."
 */
export default function GetAppPage() {
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    // Inside the native app there is nothing to download — go to the app.
    if (window.Capacitor?.isNativePlatform?.()) {
      window.location.hash = '/app';
      return;
    }
    if (isAppleDevice()) {
      setRedirecting(true);
      window.location.replace(APP_STORE_URL);
    }
  }, []);

  if (redirecting) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4 px-6">
        <div className="w-10 h-10 border-[3px] border-teal-500/20 border-t-teal-500 rounded-full animate-spin" />
        <p className="text-slate-500 text-sm text-center">
          Taking you to the App Store...{' '}
          <a href={APP_STORE_URL} className="text-teal-600 font-semibold underline">Tap here</a> if nothing happens.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50/60 to-white flex flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-sm text-center">
        <div className="flex justify-center mb-8">
          <ClariMeLogo size="default" variant="light" />
        </div>

        <h1 className="text-3xl font-bold text-slate-900 tracking-tight mb-3">Get ClariMe</h1>
        <p className="text-slate-500 leading-relaxed mb-8">
          Record your doctor visits, get a clear written summary, and share it with family.
        </p>

        <div className="mb-8">
          <AppStoreBadge className="w-full justify-center" />
          <p className="text-xs text-slate-400 mt-2">For iPhone</p>
        </div>

        <div className="flex items-center gap-3 mb-8">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">or</span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        <a
          href="#/join"
          className="block w-full px-6 py-3.5 bg-white text-slate-700 font-semibold rounded-2xl border border-slate-200 hover:border-teal-200 hover:bg-teal-50/50 transition-all mb-3"
        >
          Use ClariMe in your browser
        </a>
        <p className="text-xs text-slate-400 leading-relaxed">
          Works on Android phones and computers. No download needed.
        </p>

        <p className="text-sm text-slate-500 leading-relaxed mt-10 bg-white border border-slate-100 rounded-2xl px-5 py-4 shadow-sm">
          Sent here by your doctor? Download the app, then tap <span className="font-semibold text-slate-700">Record</span> at
          the start of your next visit.
        </p>
      </div>
    </div>
  );
}
